import React from "react";
import { Box } from "@mui/material";
import { Check } from "lucide-react";
import { CardHeader, CardShell, MONO, OutlineButton, PrimaryButton, StatusChip, T, ToolBadge } from "./chrome";
import { jiraConnectActions, type JiraProject } from "./flowStore";
import { useJiraConnectFlow } from "./useJiraConnectFlow";

const STAGE_ORDER = ["idle", "authorizing", "verifying", "selecting_project", "ready_to_sync", "importing", "synced"];

function initials(name: string): string {
    return name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? "").join("");
}

function ProjectAvatar({ project }: { project: JiraProject }) {
    return (
        <Box component="span" sx={{
            width: 24, height: 24, borderRadius: "6px", background: T.jiraBg, border: `1px solid ${T.jiraBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: T.jiraGlyph, fontSize: "11px", fontWeight: 700, flex: "0 0 auto"
        }}>
            {initials(project.name)}
        </Box>
    );
}

export default function ProjectsCard(): React.ReactElement {
    const flow = useJiraConnectFlow();
    const stageIdx = STAGE_ORDER.indexOf(flow.stage);
    const errored = flow.error?.stage === "selecting_project";
    const selecting = flow.stage === "selecting_project";
    const done = stageIdx > STAGE_ORDER.indexOf("selecting_project") && flow.selectedProject !== null;

    if (done && flow.selectedProject) {
        return (
            <CardShell testId="jc-projects">
                <Box sx={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box component="span" sx={{ color: T.success, display: "inline-flex" }}><Check size={14} strokeWidth={2.6} /></Box>
                    <Box sx={{ fontSize: "13px", fontWeight: 600, color: T.fg1 }}>Select project</Box>
                    <Box sx={{ flex: 1, fontSize: "12px", color: T.fg4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {flow.selectedProject.name} · <Box component="span" sx={{ fontFamily: MONO }}>{flow.selectedProject.key}</Box>
                    </Box>
                    <Box component="button" type="button" onClick={jiraConnectActions.changeProject} sx={{
                        border: "none", background: "none", fontSize: "11.5px", fontWeight: 500,
                        color: T.primary, cursor: "pointer", fontFamily: "inherit", flex: "0 0 auto", padding: 0
                    }}>
                        Change
                    </Box>
                </Box>
            </CardShell>
        );
    }

    if (!selecting) {
        return (
            <CardShell testId="jc-projects">
                <Box sx={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <ToolBadge name="integration_list_projects" />
                    <Box sx={{ fontSize: "12px", color: T.fg3 }}>Waiting for connection</Box>
                    <StatusChip tone="neutral" label="Pending" />
                </Box>
            </CardShell>
        );
    }

    return (
        <CardShell testId="jc-projects" active={!errored}>
            <CardHeader
                tool="integration_list_projects"
                right={errored
                    ? <StatusChip tone="error" label="Failed" />
                    : <Box component="span" sx={{ marginLeft: "auto", fontSize: "11px", fontWeight: 600, color: T.fg4 }}>
                        {flow.projects.length} project{flow.projects.length === 1 ? "" : "s"}
                    </Box>}
            />
            <Box sx={{ padding: "14px" }}>
                {errored ? (
                    <>
                        <Box sx={{ fontSize: "12px", color: T.fg3, marginBottom: "10px" }}>{flow.error?.message}</Box>
                        <OutlineButton label="Retry" onClick={jiraConnectActions.retry} />
                    </>
                ) : (
                    <>
                        <Box sx={{ fontSize: "12px", fontWeight: 600, color: T.fg2, marginBottom: "8px" }}>
                            Select a Jira project to link
                        </Box>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: "10px", border: `1px solid ${T.borderStrong}`,
                            borderRadius: "9px", background: T.bgInput, padding: "8px 12px", marginBottom: "10px"
                        }}>
                            {flow.selectedProject && <ProjectAvatar project={flow.selectedProject} />}
                            <Box
                                component="select"
                                value={flow.selectedProject?.id ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    const project = flow.projects.find(p => p.id === e.target.value);
                                    if (project) jiraConnectActions.chooseProject(project);
                                }}
                                sx={{
                                    flex: 1, border: "none", background: "transparent", outline: "none",
                                    fontSize: "13px", fontWeight: 600, color: T.fg1, fontFamily: "inherit", cursor: "pointer"
                                }}
                            >
                                <option value="" disabled>Choose a project…</option>
                                {flow.projects.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} ({p.key})</option>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: "9px" }}>
                            <PrimaryButton label="Continue" onClick={jiraConnectActions.confirmProject} disabled={!flow.selectedProject} />
                            <OutlineButton label="Switch account" onClick={jiraConnectActions.switchAccount} />
                        </Box>
                    </>
                )}
            </Box>
        </CardShell>
    );
}
